import { Injectable, InjectionToken, inject } from '@angular/core';
import { EMPTY, merge } from 'rxjs';
import { getIsNotNullable } from 'src/app/core/nullable-utils';
import { ProfileKind } from 'src/app/deliverable-profiles/review-worksheet/shared/services/topbar/topbar-api.service';
import { ENV } from 'src/app/shared/env/environment.token';
import { WorksheetStatus } from 'src/app/shared/models/review-task';
import { InsightsService } from './insights/insights.service';
import { ThirdPartyTrackingService, TrackingConfig, TrackingEvent } from './tracking.interface';

const THIRD_PARTY_TRACKING_SERVICES = new InjectionToken<ThirdPartyTrackingService[]>(
  'ThirdPartyTrackingService',
  { factory: () => [inject(InsightsService)] },
);

interface TrackingProperties {
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class TrackingService {
  readonly #thirdPartyTrackingServices = inject(THIRD_PARTY_TRACKING_SERVICES);
  readonly #env = inject(ENV);

  init() {
    if (this.#env.TRACKING_CONFIG.enabled) {
      return merge(...this.#thirdPartyTrackingServices.map(service => service.init()));
    }
    return EMPTY;
  }

  signIn(config: TrackingConfig): void {
    const { userId, emailDomain } = config;

    this.#thirdPartyTrackingServices.forEach(service => {
      service.setProfileProperties({ 'First Login': new Date().toISOString() }, { once: true });
      service.setProfileProperties({ EmailDomain: emailDomain });
      if (getIsNotNullable(userId)) {
        service.setIdentifiedUserContext(userId);
      }
    });
  }

  updateUserRoles(roles: string[]) {
    this.#thirdPartyTrackingServices.forEach(service => {
      service.setUserProperties({ Roles: roles });
    });
  }

  updateSelectedProject(projectCode: number | string | null, projectId: number | null) {
    this.#thirdPartyTrackingServices.forEach(service => {
      service.setUserProperties({ Project: projectCode, ProjectId: projectId });
    });
  }

  #sendEvent(event: TrackingEvent) {
    this.#thirdPartyTrackingServices.forEach(service => {
      service.trackEvent(event.eventName, { ...event.eventProperties });
    });
  }

  clickedOn(eventName: string, properties?: TrackingProperties): void {
    const payload: TrackingEvent = {
      eventName,
      eventProperties: {
        type: 'event',
        action: 'click',
        identifier: 'component',
        ...(properties ? properties : {}),
      },
    };

    this.#sendEvent(payload);
  }

  navigatedToPage(pageName: string, properties?: TrackingProperties): void {
    const payload: TrackingEvent = {
      eventName: `Open ${pageName}`,
      eventProperties: {
        type: 'event',
        action: 'click',
        identifier: 'component',
        ...(properties ? properties : {}),
      },
    };

    this.#sendEvent(payload);
  }

  addedRecord(name: string, properties?: TrackingProperties): void {
    const payload: TrackingEvent = {
      eventName: `Add ${name}`,
      eventProperties: {
        type: 'event',
        action: 'click',
        identifier: 'component',
        ...(properties ? properties : {}),
      },
    };

    this.#sendEvent(payload);
  }

  submitFor(state: WorksheetStatus, type: ProfileKind): void {
    let stateString: string = state;

    switch (state) {
      case 'IN_PROGRESS':
        stateString = 'Progress';
        break;
      case 'IN_QA':
        stateString = 'QA';
        break;
      case 'IN_REVIEW':
        stateString = 'Review';
        break;
    }

    const payload: TrackingEvent = {
      eventName: `Submit Deliverable for ${stateString}`,
      eventProperties: {
        type: 'event',
        action: 'click',
        identifier: 'component',
        value: type,
      },
    };

    this.#sendEvent(payload);
  }
}
